/**
 * Deploy hook — bridges agent mutations to the persistent deploy drawer.
 *
 * Usage:
 *   const deploy = useDeploy();
 *   deploy.start(jobId, { kind: 'create', agent: 'news' });
 *
 * The drawer subscribes to `deployStore`, so components never touch the
 * drawer directly — they just call `start(jobId)` after their mutation
 * returns a 202. `useDeployPoller` drives the state machine by polling
 * `/api/deploy/{id}` every second for v1; the WS bus replaces this later.
 */
import { useCallback, useEffect } from 'react';
import { deployApi, type DeployJob } from '@/api/deploy';
import { useDeployStore } from '@/stores/deployStore';

export interface StartDeployOptions {
  kind: string;
  agent?: string;
  label?: string;
}

export function useDeploy() {
  const start = useDeployStore((s) => s.start);
  const setStatus = useDeployStore((s) => s.setStatus);

  const begin = useCallback(
    (jobId: string, opts: StartDeployOptions) => {
      start(jobId, opts);
      setStatus('pending');
    },
    [start, setStatus]
  );

  return { start: begin };
}

/** Mount this once at the app shell level to drive deploy state. */
export function useDeployPoller() {
  const jobId = useDeployStore((s) => s.activeJobId);
  const apply = useDeployStore((s) => s.applyJob);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const job: DeployJob = await deployApi.get(jobId);
        if (cancelled) return;
        apply(job);
        if (job.status === 'running' || job.status === 'pending') {
          setTimeout(tick, 1000);
        }
      } catch (err) {
        if (cancelled) return;
        // Transient error — retry with backoff.
        setTimeout(tick, 3000);
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [jobId, apply]);
}
